import { motion } from "framer-motion";
import { Button } from "@/projects/NPS/Stepper/common/button";
import { Badge } from "@/projects/NPS/Stepper/components/ui/badge";

export const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-nps-gradient pb-20 pt-12">
      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-xl space-y-8"
          >
            {/* Badge */}
            <Badge className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
              <span className="h-2 w-2 animate-pulse rounded-full bg-green-400" />
              India&apos;s Trusted Investment Platform
            </Badge>

            {/* Main Heading */}
            <h1 className="font-nunito text-3xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl xl:text-[52px]">
              Your Digital{" "}
              <span className="font-nunito text-npsText">Wealth Mall</span>
            </h1>

            {/* Sub Heading */}
            <h2 className="max-w-xl font-nunito text-xl font-medium leading-relaxed text-white/85 md:text-2xl">
              Diversify Assets, Multiply Opportunities, Grow Your Wealth
            </h2>

            {/* Feature List */}
            <ul className="space-y-3.5 text-sm text-white/85 md:text-base">
              <li className="flex items-start gap-3.5">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-trending-up h-4 w-4"
                    aria-hidden="true"
                  >
                    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                    <polyline points="16 7 22 7 22 13" />
                  </svg>
                </div>
                <span className="my-auto font-nunito">
                  IPOs, Bonds, FDs &amp; NPS in one platform
                </span>
              </li>

              <li className="flex items-start gap-3.5">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-shield h-4 w-4"
                    aria-hidden="true"
                  >
                    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                  </svg>
                </div>
                <span className="my-auto font-nunito">
                  SEBI &amp; RBI compliant investments
                </span>
              </li>

              <li className="flex items-start gap-3.5">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-percent h-4 w-4"
                    aria-hidden="true"
                  >
                    <line x1="19" x2="5" y1="5" y2="19" />
                    <circle cx="6.5" cy="6.5" r="2.5" />
                    <circle cx="17.5" cy="17.5" r="2.5" />
                  </svg>
                </div>
                <span className="my-auto font-nunito">
                  Earn up to 9.10% on Fixed Deposits
                </span>
              </li>
            </ul>

            {/* Buttons */}
            <div className="flex flex-wrap gap-4 pt-3">
              <Button
                type="button"
                className="items-cente f ocus-visible:ring-offset-2 inline-flex h-12 justify-center gap-2 whitespace-nowrap rounded-xl bg-npsText px-8 py-[26px] font-nunito text-base font-bold text-white shadow-lg ring-offset-background transition-all duration-200 hover:bg-npsText/90 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                role="button"
              >
                Start Investing
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-arrow-right h-4 w-4"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </Button>

              <Button
                type="button"
                className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-xl border-2 border-white/30 bg-white/10 px-8 py-6 font-nunito text-base font-bold text-white ring-offset-background backdrop-blur-sm transition-all duration-200 hover:border-white/50 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                role="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-building2 h-4 w-4"
                >
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
                  <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
                  <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
                  <path d="M10 6h4" />
                  <path d="M10 10h4" />
                  <path d="M10 14h4" />
                  <path d="M10 18h4" />
                </svg>
                Open NPS Account
              </Button>
            </div>

            {/* Stats Row */}
            <div className="flex flex-wrap justify-between gap-6 border-t border-white/10 pt-6 lg:gap-10">
              <div className="group min-w-[120px] text-center sm:text-left">
                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-users h-5 w-5 text-primary opacity-80"
                    aria-hidden="true"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  <p className="font-nunito text-2xl font-bold tracking-tight text-white md:text-3xl">
                    50K+
                  </p>
                </div>
                <p className="mt-1 font-nunito text-sm font-medium text-white/60">
                  Active Users
                </p>
              </div>

              <div className="group min-w-[120px] text-center sm:text-left">
                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-award h-5 w-5 text-primary opacity-80"
                    aria-hidden="true"
                  >
                    <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526" />
                    <circle cx="12" cy="8" r="6" />
                  </svg>
                  <p className="font-nunito text-2xl font-bold tracking-tight text-white md:text-3xl">
                    ₹100Cr+
                  </p>
                </div>
                <p className="mt-1 font-nunito text-sm font-medium text-white/60">
                  AUM
                </p>
              </div>

              <div className="group min-w-[120px] text-center sm:text-left">
                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-star h-5 w-5 text-amber-400"
                    aria-hidden="true"
                  >
                    <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                  </svg>
                  <p className="font-nunito text-2xl font-bold tracking-tight text-white md:text-3xl">
                    4.8
                  </p>
                </div>
                <p className="mt-1 font-nunito text-sm font-medium text-white/60">
                  User Rating
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative flex justify-center lg:justify-end"
          >
            {/* Background Glow */}
            <div className="absolute inset-0 rounded-full bg-primary/20 opacity-30 blur-3xl" />

            {/* Illustration */}
            <div className="relative">
              <motion.img
                src="/assets/hero-illustration-Rfu5WWtS.png"
                alt="Investment illustration"
                className="w-full max-w-sm object-contain drop-shadow-2xl md:max-w-md lg:max-w-lg"
                animate={{
                  y: [0, -12, 0], // Moves up and down
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* IPO Returns Card (TOP-LEFT on image now) */}
              <div className="animate-fade-in absolute -left-6 -top-6 hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-xl lg:block">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="25"
                      height="25"
                      fill="none"
                      stroke="green"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-green-600"
                    >
                      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                      <polyline points="16 7 22 7 22 13" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-nunito text-xs font-medium text-gray-500">
                      IPO Returns
                    </p>
                    <p className="font-nunito text-base font-bold text-green-600">
                      +45.2%
                    </p>
                  </div>
                </div>
              </div>

              {/* FD Interest Card (BOTTOM-RIGHT inside image now) */}
              <div
                className="animate-fade-in absolute bottom-4 right-1 hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-xl lg:block"
                style={{ animationDelay: "0.2s" }}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="25"
                      height="25"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-green-600"
                    >
                      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-nunito text-xs font-medium text-gray-500">
                      FD Interest
                    </p>
                    <p className="font-nunito text-base font-bold text-green-600">
                      9.10% p.a.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
